import { useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const BASE_URL = window.location.origin;

// ─────────────────────────────────────────────────────────────
// BACKGROUND PATTERNS — fill entire 900×636 certificate
// Rendered at low opacity as watermark behind content
// Logo is placed separately in center at visible opacity
// ─────────────────────────────────────────────────────────────

function BgGuilloche({ c }) {
  return (
    <g>
      {Array.from({ length: 30 }, (_, i) => {
        const y = i * 22;
        return (
          <path key={`a${i}`}
            d={`M0,${y} Q225,${y-28} 450,${y} Q675,${y+28} 900,${y}`}
            fill="none" stroke={c} strokeWidth="1.4" strokeLinecap="round" opacity="0.55"/>
        );
      })}
      {Array.from({ length: 30 }, (_, i) => {
        const y = i * 22 - 11;
        return (
          <path key={`b${i}`}
            d={`M0,${y} Q225,${y+28} 450,${y} Q675,${y-28} 900,${y}`}
            fill="none" stroke={c} strokeWidth="0.7" strokeLinecap="round" opacity="0.3"/>
        );
      })}
      {/* Double outer frame */}
      <rect x="14" y="10" width="872" height="616" rx="4" fill="none" stroke={c} strokeWidth="2" opacity="0.6"/>
      <rect x="22" y="18" width="856" height="600" rx="3" fill="none" stroke={c} strokeWidth="0.8" opacity="0.3"/>
      {/* Corner L-brackets */}
      {[[14,10],[886,10],[14,626],[886,626]].map(([px,py],i) => (
        <g key={i}>
          <line x1={px} y1={py} x2={px+(i%2===0?28:-28)} y2={py} stroke={c} strokeWidth="3" opacity="0.8"/>
          <line x1={px} y1={py} x2={px} y2={py+(i<2?28:-28)} stroke={c} strokeWidth="3" opacity="0.8"/>
        </g>
      ))}
    </g>
  );
}

function BgMosaic({ c }) {
  const fills = ['#1a0a02','#0a1a08','#0a0818','#180a0a','#08180a','#100810','#0a1018','#180a10'];
  return (
    <g>
      {[0,1,2,3,4,5].map(row =>
        [0,1,2,3,4,5,6,7].map(col => {
          const x = col * 115 + (row%2)*57;
          const y = row * 100 - 50;
          const pts = `${x+57},${y} ${x+115},${y+33} ${x+115},${y+100} ${x+57},${y+133} ${x},${y+100} ${x},${y+33}`;
          return (
            <g key={`${row}-${col}`}>
              <polygon points={pts} fill={fills[(row+col)%8]} stroke={c} strokeWidth="1.5" opacity="0.7"/>
              <line x1={x+25} y1={y+12} x2={x+55} y2={y+58} stroke={c} strokeWidth="0.5" opacity="0.4"/>
              <line x1={x+80} y1={y+16} x2={x+62} y2={y+62} stroke={c} strokeWidth="0.5" opacity="0.3"/>
            </g>
          );
        })
      )}
      <rect x="14" y="10" width="872" height="616" rx="4" fill="none" stroke={c} strokeWidth="2" opacity="0.5"/>
    </g>
  );
}

function BgMosaicLight({ c }) {
  const fills = ['#f5e6c0','#d4ecd4','#d4d4f0','#f0d4d4','#d4ead4','#f0d8c0','#e8d4f0','#d4e8f0'];
  return (
    <g>
      {[0,1,2,3,4,5].map(row =>
        [0,1,2,3,4,5,6,7].map(col => {
          const x = col * 115 + (row%2)*57;
          const y = row * 100 - 50;
          const pts = `${x+57},${y} ${x+115},${y+33} ${x+115},${y+100} ${x+57},${y+133} ${x},${y+100} ${x},${y+33}`;
          return (
            <g key={`${row}-${col}`}>
              <polygon points={pts} fill={fills[(row+col)%8]} stroke={c} strokeWidth="1.5" opacity="0.65"/>
              <line x1={x+25} y1={y+12} x2={x+55} y2={y+58} stroke={c} strokeWidth="0.5" opacity="0.35"/>
            </g>
          );
        })
      )}
      <rect x="14" y="10" width="872" height="616" rx="4" fill="none" stroke={c} strokeWidth="2" opacity="0.5"/>
    </g>
  );
}

function BgRope({ c }) {
  return (
    <g>
      {Array.from({ length: 22 }, (_, i) => (
        <line key={i} x1={i*48-200} y1="0" x2={i*48+636} y2="636" stroke={c} strokeWidth="0.7" opacity="0.2"/>
      ))}
      <path d="M0,36 Q56,24 112,36 Q168,48 224,36 Q280,24 336,36 Q392,48 448,36 Q504,24 560,36 Q616,48 672,36 Q728,24 784,36 Q840,48 900,36"
        fill="none" stroke={c} strokeWidth="5" strokeLinecap="round" opacity="0.8"/>
      <path d="M0,45 Q56,57 112,45 Q168,33 224,45 Q280,57 336,45 Q392,33 448,45 Q504,57 560,45 Q616,33 672,45 Q728,57 784,45 Q840,33 900,45"
        fill="none" stroke={c} strokeWidth="3" strokeLinecap="round" opacity="0.55"/>
      <path d="M0,594 Q56,582 112,594 Q168,606 224,594 Q280,582 336,594 Q392,606 448,594 Q504,582 560,594 Q616,606 672,594 Q728,582 784,594 Q840,606 900,594"
        fill="none" stroke={c} strokeWidth="5" strokeLinecap="round" opacity="0.8"/>
      <path d="M0,603 Q56,615 112,603 Q168,591 224,603 Q280,615 336,603 Q392,591 448,603 Q504,615 560,603 Q616,591 672,603 Q728,615 784,603 Q840,591 900,603"
        fill="none" stroke={c} strokeWidth="3" strokeLinecap="round" opacity="0.55"/>
      <path d="M38,45 Q32,100 38,160 Q44,220 38,280 Q32,340 38,400 Q44,460 38,520 Q32,570 38,594"
        fill="none" stroke={c} strokeWidth="5" strokeLinecap="round" opacity="0.8"/>
      <path d="M47,45 Q53,100 47,160 Q41,220 47,280 Q53,340 47,400 Q41,460 47,520 Q53,570 47,594"
        fill="none" stroke={c} strokeWidth="3" strokeLinecap="round" opacity="0.55"/>
      <path d="M862,45 Q856,100 862,160 Q868,220 862,280 Q856,340 862,400 Q868,460 862,520 Q856,570 862,594"
        fill="none" stroke={c} strokeWidth="5" strokeLinecap="round" opacity="0.8"/>
      <path d="M871,45 Q877,100 871,160 Q865,220 871,280 Q877,340 871,400 Q865,460 871,520 Q877,570 871,594"
        fill="none" stroke={c} strokeWidth="3" strokeLinecap="round" opacity="0.55"/>
      {[[38,38],[862,38],[38,598],[862,598]].map(([rx,ry],i)=>(
        <g key={i} transform={`translate(${rx},${ry})`}>
          <circle r="14" fill={c} opacity="0.7"/>
          <circle r="8" fill="rgba(0,0,0,0.6)"/>
          <circle r="4" fill={c} opacity="0.9"/>
        </g>
      ))}
      <rect x="60" y="58" width="780" height="520" rx="3" fill="none" stroke={c} strokeWidth="1" opacity="0.35"/>
    </g>
  );
}

function BgRadiant({ c }) {
  const cx = 450, cy = 318;
  return (
    <g>
      {Array.from({ length: 40 }, (_, i) => {
        const a = i * 9 * Math.PI / 180;
        return <line key={i} x1={cx} y1={cy} x2={cx+Math.cos(a)*700} y2={cy+Math.sin(a)*700}
          stroke={c} strokeWidth="0.8" opacity="0.22"/>;
      })}
      {[70,140,210,280,350,420].map(r => (
        <polygon key={r} stroke={c} strokeWidth={r<140?1.8:r<280?1:0.6}
          fill={r===70?`${c}15`:'none'}
          points={Array.from({length:6},(_,i)=>{const a=i*60*Math.PI/180-Math.PI/6; return `${cx+r*Math.cos(a)},${cy+r*Math.sin(a)}`;}).join(' ')}
          opacity={r===70?0.9:0.45}/>
      ))}
      {Array.from({length:6},(_,i)=>{
        const a=i*60*Math.PI/180;
        return <line key={i} x1={cx+140*Math.cos(a)} y1={cy+140*Math.sin(a)}
          x2={cx+210*Math.cos(a)} y2={cy+210*Math.sin(a)} stroke={c} strokeWidth="2" opacity="0.5"/>;
      })}
      {[[60,30],[840,30],[60,606],[840,606]].map(([dx,dy],i)=>(
        <g key={i} transform={`translate(${dx},${dy})`}>
          <polygon points="0,-18 13,0 0,18 -13,0" fill={`${c}30`} stroke={c} strokeWidth="1.5" opacity="0.8"/>
          <polygon points="0,-9 6,0 0,9 -6,0" fill={c} opacity="0.6"/>
        </g>
      ))}
      <line x1="0" y1="0" x2="450" y2="636" stroke={c} strokeWidth="0.8" opacity="0.12"/>
      <line x1="900" y1="0" x2="450" y2="636" stroke={c} strokeWidth="0.8" opacity="0.12"/>
      <line x1="0" y1="636" x2="450" y2="0" stroke={c} strokeWidth="0.8" opacity="0.12"/>
      <line x1="900" y1="636" x2="450" y2="0" stroke={c} strokeWidth="0.8" opacity="0.12"/>
      <rect x="14" y="10" width="872" height="616" rx="4" fill="none" stroke={c} strokeWidth="1.5" opacity="0.4"/>
    </g>
  );
}

function BgJewel({ c }) {
  const hp = (hx,hy,r) => Array.from({length:6},(_,i)=>{const a=i*60*Math.PI/180-Math.PI/6; return `${hx+r*Math.cos(a)},${hy+r*Math.sin(a)}`;}).join(' ');
  const jewels = [
    {x:120,y:130,fill:'#1a0808',sc:'#c04040'},{x:290,y:130,fill:'#100818',sc:'#6030c0'},
    {x:460,y:130,fill:'#081808',sc:'#30a060'},{x:630,y:130,fill:'#181008',sc:'#c09020'},
    {x:800,y:130,fill:'#080818',sc:'#3050c0'},
    {x:205,y:248,fill:'#180818',sc:'#c04080'},{x:375,y:248,fill:'#0a1808',sc:'#308040'},
    {x:545,y:248,fill:'#180a08',sc:'#c06020'},{x:715,y:248,fill:'#081018',sc:'#105080'},
    {x:120,y:366,fill:'#100818',sc:'#6030c0'},{x:290,y:366,fill:'#081808',sc:'#30a060'},
    {x:460,y:366,fill:'#1a0808',sc:'#c04040'},{x:630,y:366,fill:'#080818',sc:'#3050c0'},
    {x:800,y:366,fill:'#181008',sc:'#c09020'},
    {x:205,y:484,fill:'#0a1808',sc:'#308040'},{x:375,y:484,fill:'#180a08',sc:'#c06020'},
    {x:545,y:484,fill:'#100818',sc:'#6030c0'},{x:715,y:484,fill:'#1a0808',sc:'#c04040'},
  ];
  return (
    <g>
      {jewels.map(({x,y,fill,sc},i)=>(
        <g key={i}>
          <polygon points={hp(x,y,68)} fill={fill} stroke={sc} strokeWidth="2.5" opacity="0.8"/>
          <polygon points={hp(x,y,54)} fill="none" stroke={sc} strokeWidth="0.7" opacity="0.45"/>
          <line x1={x-22} y1={y-38} x2={x-7} y2={y+10} stroke={sc} strokeWidth="0.5" opacity="0.6"/>
          <line x1={x+16} y1={y-40} x2={x+5} y2={y+12} stroke={sc} strokeWidth="0.5" opacity="0.5"/>
        </g>
      ))}
      <circle cx="450" cy="318" r="260" fill="none" stroke={c} strokeWidth="2" opacity="0.35"/>
      <circle cx="450" cy="318" r="248" fill="none" stroke={c} strokeWidth="0.8" strokeDasharray="8 5" opacity="0.2"/>
      <rect x="14" y="10" width="872" height="616" rx="4" fill="none" stroke={c} strokeWidth="1.5" opacity="0.45"/>
    </g>
  );
}

function BgWave({ c }) {
  return (
    <g>
      {Array.from({length:32},(_,i)=>{
        const y=i*21;
        return <path key={i} d={`M0,${y} C150,${y-22} 300,${y+22} 450,${y} S750,${y-22} 900,${y}`}
          fill="none" stroke={c} strokeWidth={i%3===0?1.4:0.7} strokeLinecap="round" opacity={i%3===0?0.5:0.28}/>;
      })}
      {Array.from({length:16},(_,col)=>{
        const x=col*58,y=0;
        return <polygon key={col} points={`${x+29},${y} ${x+58},${y+17} ${x+58},${y+52} ${x+29},${y+69} ${x},${y+52} ${x},${y+17}`}
          fill="none" stroke={c} strokeWidth="0.8" opacity="0.4"/>;
      })}
      {Array.from({length:16},(_,col)=>{
        const x=col*58,y=567;
        return <polygon key={col} points={`${x+29},${y} ${x+58},${y+17} ${x+58},${y+52} ${x+29},${y+69} ${x},${y+52} ${x},${y+17}`}
          fill="none" stroke={c} strokeWidth="0.8" opacity="0.4"/>;
      })}
      <path d="M50,16 Q450,4 850,16" fill="none" stroke={c} strokeWidth="2" opacity="0.6"/>
      <path d="M50,622 Q450,634 850,622" fill="none" stroke={c} strokeWidth="2" opacity="0.6"/>
      <path d="M22,20 Q14,318 22,618" fill="none" stroke={c} strokeWidth="2" opacity="0.6"/>
      <path d="M878,20 Q886,318 878,618" fill="none" stroke={c} strokeWidth="2" opacity="0.6"/>
      <path d="M22,18 Q32,8 46,18 Q58,28 46,40" fill="none" stroke={c} strokeWidth="1.8" opacity="0.7"/>
      <path d="M878,18 Q868,8 854,18 Q842,28 854,40" fill="none" stroke={c} strokeWidth="1.8" opacity="0.7"/>
      <path d="M22,620 Q32,630 46,620 Q58,610 46,598" fill="none" stroke={c} strokeWidth="1.8" opacity="0.7"/>
      <path d="M878,620 Q868,630 854,620 Q842,610 854,598" fill="none" stroke={c} strokeWidth="1.8" opacity="0.7"/>
    </g>
  );
}

function BgArch({ c }) {
  const cols = [65,130,195,260,325,390,455,520,585,650,715,780,845];
  return (
    <g>
      {cols.map(x=>(
        <path key={x} d={`M${x-32},636 Q${x-32},500 ${x-32},420 Q${x-32},358 ${x},332 Q${x+32},358 ${x+32},420 L${x+32},636`}
          fill="none" stroke={c} strokeWidth="1" opacity="0.5"/>
      ))}
      {[420,470,520,568,610].map(y=>(
        <line key={y} x1="0" y1={y} x2="900" y2={y} stroke={c} strokeWidth="0.5" opacity="0.28"/>
      ))}
      {cols.map(x=>(
        <g key={`t${x}`}>
          <circle cx={x} cy={342} r={18} fill={`${c}15`} stroke={c} strokeWidth="1.2" opacity="0.8"/>
          <circle cx={x} cy={322} r={13} fill={`${c}10`} stroke={c} strokeWidth="0.9" opacity="0.65"/>
          <circle cx={x-13} cy={335} r={10} fill="none" stroke={c} strokeWidth="0.7" opacity="0.55"/>
          <circle cx={x+13} cy={335} r={10} fill="none" stroke={c} strokeWidth="0.7" opacity="0.55"/>
        </g>
      ))}
      {Array.from({length:12},(_,i)=>{
        const a=i*30*Math.PI/180;
        const rx=450+68*Math.cos(a), ry=180+68*Math.sin(a);
        return <ellipse key={i} cx={rx} cy={ry} rx={15} ry={30}
          fill={`${c}10`} stroke={c} strokeWidth="0.9" opacity="0.65"
          transform={`rotate(${i*30} ${rx} ${ry})`}/>;
      })}
      <circle cx="450" cy="180" r="84" fill="none" stroke={c} strokeWidth="1.5" opacity="0.55"/>
      <circle cx="450" cy="180" r="62" fill={`${c}08`} stroke={c} strokeWidth="0.8" opacity="0.45"/>
      <rect x="14" y="10" width="872" height="616" rx="4" fill="none" stroke={c} strokeWidth="1.5" opacity="0.45"/>
    </g>
  );
}

const BG_MAP = {
  guilloche: BgGuilloche, mosaic: BgMosaic, 'mosaic-light': BgMosaicLight,
  rope: BgRope, radiant: BgRadiant, jewel: BgJewel, wave: BgWave, arch: BgArch,
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export default function CertificateTemplate({ cert, onClose, inline = false }) {
  const certRef  = useRef();
  const [downloading, setDownloading] = useState(false);

  const primaryColor    = cert.primary_color    || '#e8a43a';
  const titleText       = cert.title_text       || 'Certificate of Completion';
  const bodyText        = cert.body_text        || 'has successfully completed the program';
  const signatoryName   = cert.signatory_name   || 'MilleHills Ltd';
  const signatoryTitle  = cert.signatory_title  || 'Issuing Authority';
  const signatoryPerson = cert.signatory_person || '';   // actual name e.g. "Jean-Paul Uwimana"
  const signatory2Name  = cert.signatory_name2  || '';   // second signatory org
  const signatory2Title = cert.signatory_title2 || '';
  const signatory2Person= cert.signatory_person2|| '';   // second signatory person name
  const signatureUrl    = cert.signature_url    || null;
  const logoUrl         = cert.logo_url         || null;
  const rawStyle       = cert.background_style || 'guilloche';
  const isLight        = rawStyle.endsWith('-light');
  const bgKey          = rawStyle.replace(/-light$/, '').replace(/-dark$/, '') || 'guilloche';
  const BgComp         = BG_MAP[rawStyle] || BG_MAP[bgKey] || BgGuilloche;

  const bgColor    = isLight ? '#faf7f0' : '#0d0f12';
  const textColor  = isLight ? '#1a1208' : '#ffffff';
  const mutedColor = isLight ? 'rgba(30,18,4,0.45)'  : 'rgba(255,255,255,0.45)';
  const subtleColor= isLight ? 'rgba(30,18,4,0.7)'   : 'rgba(255,255,255,0.7)';
  const borderColor= `${primaryColor}40`;

  const certUrl   = `${BASE_URL}/verify/${cert.cert_hash}`;
  const issueDate = cert.issued_at
    ? new Date(cert.issued_at).toLocaleDateString('en-RW', { year:'numeric', month:'long', day:'numeric' })
    : '—';

  const download = async () => {
    setDownloading(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF }       = await import('jspdf');
      const canvas = await html2canvas(certRef.current, {
        scale: 3, useCORS: true, allowTaint: true, backgroundColor: bgColor, logging: false,
      });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const w = pdf.internal.pageSize.getWidth();
      const h = pdf.internal.pageSize.getHeight();
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h);
      pdf.save(`MilleHills-${cert.recipient_name.replace(/\s+/g, '-')}.pdf`);
    } catch { alert('Download failed. Please try again.'); }
    finally { setDownloading(false); }
  };

  const CertBody = (
    <div ref={certRef} style={{
      width: 900, height: 636,
      background: bgColor,
      position: 'relative', overflow: 'hidden',
      fontFamily: "'DM Sans', sans-serif",
      flexShrink: 0,
    }}>

      {/* ── LAYER 1: Background pattern (entire certificate) ── */}
      <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:1 }}
        viewBox="0 0 900 636" preserveAspectRatio="xMidYMid slice">
        <g opacity="0.15">
          <BgComp c={primaryColor}/>
        </g>
      </svg>

      {/* ── LAYER 2: Logo centered, visible ── */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        {logoUrl ? (
          <img src={logoUrl} alt="Watermark"
            style={{ width: 200, height: 200, objectFit: 'contain', opacity: 0.07 }}/>
        ) : (
          <div style={{
            fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 260,
            color: primaryColor, opacity: 0.07, lineHeight: 1,
            userSelect: 'none',
          }}>M</div>
        )}
      </div>

      {/* ── LAYER 3: Top accent bar ── */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:4, background:`linear-gradient(90deg, ${primaryColor}, ${primaryColor}bb 50%, ${primaryColor})`, zIndex:4 }}/>
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:2, background:`${primaryColor}44`, zIndex:4 }}/>

      {/* ── LAYER 4: Border frames ── */}
      <div style={{ position:'absolute', inset:14, border:`1.5px solid ${borderColor}`, borderRadius:4, pointerEvents:'none', zIndex:4 }}/>
      <div style={{ position:'absolute', inset:20, border:`0.5px solid ${isLight?primaryColor+'20':primaryColor+'12'}`, borderRadius:2, pointerEvents:'none', zIndex:4 }}/>

      {/* ── LAYER 5: Corner ornaments ── */}
      {[{top:14,left:14},{top:14,right:14},{bottom:14,left:14},{bottom:14,right:14}].map((pos,i)=>(
        <div key={i} style={{ position:'absolute', width:36, height:36, opacity:0.5, zIndex:5, ...pos }}>
          <svg viewBox="0 0 36 36" fill="none">
            <path fill={primaryColor} d={
              i===0?"M0 0 L36 0 L36 7 L7 7 L7 36 L0 36 Z":
              i===1?"M36 0 L0 0 L0 7 L29 7 L29 36 L36 36 Z":
              i===2?"M0 36 L36 36 L36 29 L7 29 L7 0 L0 0 Z":
                    "M36 36 L0 36 L0 29 L29 29 L29 0 L36 0 Z"
            }/>
          </svg>
        </div>
      ))}

      {/* ── LAYER 6: Certificate content ── */}
      <div style={{ position:'relative', zIndex:6, height:'100%', display:'flex', flexDirection:'column', padding:'24px 52px 20px' }}>

        {/* HEADER */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10, flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {logoUrl
              ? <img src={logoUrl} alt="Logo" style={{ height:42, width:'auto', objectFit:'contain' }}/>
              : <div style={{ width:38, height:38, background:primaryColor, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:18, color:isLight?'#fff':'#0d0f12', fontFamily:"'Syne',sans-serif" }}>M</div>
            }
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15, color:textColor }}>MilleHills</div>
              <div style={{ fontSize:8, color:mutedColor, letterSpacing:'0.14em', textTransform:'uppercase' }}>Gisenyi · Rwanda</div>
            </div>
          </div>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize:8, fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase', color:primaryColor, marginBottom:2 }}>{titleText}</div>
            <div style={{ fontSize:7, color:mutedColor, fontFamily:'monospace' }}>{cert.cert_hash?.slice(0,18)}…</div>
          </div>
        </div>

        {/* Header divider */}
        <div style={{ height:1, background:`linear-gradient(90deg, transparent, ${primaryColor}55 20%, ${primaryColor}55 80%, transparent)`, marginBottom:0, flexShrink:0 }}/>

        {/* CENTER BLOCK — takes all remaining space, vertically centers content */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12 }}>

          {/* RECIPIENT NAME */}
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:9, fontWeight:600, letterSpacing:'0.26em', textTransform:'uppercase', color:mutedColor, marginBottom:7 }}>
              This is to certify that
            </div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:38, fontWeight:800, color:primaryColor, lineHeight:1.1, marginBottom:3, letterSpacing:'-0.01em' }}>
              {cert.recipient_name}
            </div>
            <div style={{ fontSize:9, color:mutedColor, letterSpacing:'0.05em' }}>
              {cert.recipient_email}
            </div>
          </div>

          {/* ORNAMENTAL DIVIDER */}
          <div style={{ display:'flex', alignItems:'center', gap:14, width:'100%' }}>
            <div style={{ flex:1, height:1, background:`linear-gradient(to right, transparent, ${primaryColor}55)` }}/>
            <span style={{ fontSize:16, color:primaryColor, opacity:0.85, lineHeight:1 }}>◈</span>
            <div style={{ flex:1, height:1, background:`linear-gradient(to left, transparent, ${primaryColor}55)` }}/>
          </div>

          {/* BODY ROW: body text centered, seal+QR to the right */}
          <div style={{ display:'flex', alignItems:'center', gap:28, width:'100%', justifyContent:'center' }}>

            {/* Body text + program */}
            <div style={{ textAlign:'center', flex:1, maxWidth:500 }}>
              <div style={{ fontSize:12, color:subtleColor, lineHeight:1.7, marginBottom:6 }}>
                {bodyText}
              </div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:21, fontWeight:700, color:textColor, lineHeight:1.3, marginBottom:cert.cohort_name?4:0 }}>
                {cert.program_title}
              </div>
              {cert.cohort_name && (
                <div style={{ fontSize:9, color:mutedColor, letterSpacing:'0.06em', marginTop:4 }}>
                  Cohort: {cert.cohort_name}
                  {cert.start_date && cert.end_date && (
                    <span style={{ marginLeft:8 }}>
                      {new Date(cert.start_date).toLocaleDateString('en-RW',{month:'short',year:'numeric'})} – {new Date(cert.end_date).toLocaleDateString('en-RW',{month:'short',year:'numeric'})}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Vertical separator */}
            <div style={{ width:1, height:80, background:`${primaryColor}28`, flexShrink:0 }}/>

            {/* Seal + QR */}
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:7, flexShrink:0, width:90 }}>
              <div style={{ position:'relative', width:68, height:68 }}>
                <svg width="68" height="68" viewBox="0 0 68 68" style={{ position:'absolute', inset:0 }}>
                  <circle cx="34" cy="34" r="32" fill="none" stroke={primaryColor} strokeWidth="1" opacity="0.5" strokeDasharray="3 2.5"/>
                  <circle cx="34" cy="34" r="25" fill="none" stroke={primaryColor} strokeWidth="0.7" opacity="0.25"/>
                  {Array.from({length:8},(_,i)=>{
                    const a=(i*45-90)*Math.PI/180;
                    return <line key={i} x1={34+19*Math.cos(a)} y1={34+19*Math.sin(a)} x2={34+25*Math.cos(a)} y2={34+25*Math.sin(a)} stroke={primaryColor} strokeWidth="1.2" opacity="0.55"/>;
                  })}
                </svg>
                <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                  {logoUrl
                    ? <img src={logoUrl} alt="Logo" style={{ width:30, height:30, objectFit:'contain' }}/>
                    : <><div style={{ fontSize:17, color:primaryColor, fontFamily:"'Syne',sans-serif", fontWeight:800, lineHeight:1 }}>M</div>
                        <div style={{ fontSize:5, color:`${primaryColor}bb`, letterSpacing:'0.1em', textTransform:'uppercase', marginTop:1 }}>MilleHills</div></>
                  }
                </div>
              </div>
              <div style={{ background:'#fff', padding:4, borderRadius:5 }}>
                <QRCodeSVG value={certUrl} size={52} level="H" includeMargin={false}
                  imageSettings={logoUrl?{src:logoUrl,height:11,width:11,excavate:true}:undefined}
                />
              </div>
              <div style={{ fontSize:6, color:mutedColor, letterSpacing:'0.08em', textTransform:'uppercase' }}>Scan to verify</div>
            </div>
          </div>
        </div>

        {/* SIGNATORIES — pinned at bottom, always centered */}
        <div style={{ flexShrink:0 }}>
          <div style={{ height:1, background:`${primaryColor}25`, marginBottom:10 }}/>
          <div style={{ display:'flex', justifyContent:'center', alignItems:'flex-start', gap:60 }}>

            {/* Left signatory */}
            <div style={{ textAlign:'center', minWidth:130 }}>
              {signatureUrl
                ? <img src={signatureUrl} alt="Sig" style={{ height:22, display:'block', margin:'0 auto 4px', opacity:0.85 }}/>
                : <div style={{ width:120, height:1, background:`${primaryColor}55`, margin:'0 auto 5px' }}/>
              }
              {signatoryPerson && (
                <div style={{ fontSize:11, fontWeight:700, color:subtleColor, marginBottom:1 }}>{signatoryPerson}</div>
              )}
              <div style={{ fontSize:10, fontWeight:signatoryPerson?400:600, color:signatoryPerson?mutedColor:subtleColor }}>{signatoryName}</div>
              <div style={{ fontSize:8, color:mutedColor, letterSpacing:'0.07em', textTransform:'uppercase', marginTop:1 }}>{signatoryTitle}</div>
            </div>

            {/* Center — date */}
            <div style={{ textAlign:'center', minWidth:130 }}>
              <div style={{ width:120, height:1, background:`${primaryColor}55`, margin:'0 auto 5px' }}/>
              <div style={{ fontSize:10, fontWeight:600, color:subtleColor }}>{issueDate}</div>
              <div style={{ fontSize:8, color:mutedColor, letterSpacing:'0.07em', textTransform:'uppercase', marginTop:1 }}>Date of Issue</div>
            </div>

            {/* Right signatory — only if set */}
            {(signatory2Name || signatory2Person) && (
              <div style={{ textAlign:'center', minWidth:130 }}>
                <div style={{ width:120, height:1, background:`${primaryColor}55`, margin:'0 auto 5px' }}/>
                {signatory2Person && (
                  <div style={{ fontSize:11, fontWeight:700, color:subtleColor, marginBottom:1 }}>{signatory2Person}</div>
                )}
                {signatory2Name && (
                  <div style={{ fontSize:10, fontWeight:signatory2Person?400:600, color:signatory2Person?mutedColor:subtleColor }}>{signatory2Name}</div>
                )}
                {signatory2Title && (
                  <div style={{ fontSize:8, color:mutedColor, letterSpacing:'0.07em', textTransform:'uppercase', marginTop:1 }}>{signatory2Title}</div>
                )}
              </div>
            )}
          </div>

          <div style={{ marginTop:8, display:'flex', justifyContent:'space-between', fontSize:7, color:isLight?'rgba(30,18,4,0.2)':'rgba(255,255,255,0.18)' }}>
            <span>Verify: {certUrl.replace('https://','')}</span>
            <span>Founder-Led · Constitution-Protected · Gisenyi, Rwanda</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (inline) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:14 }}>
        <div style={{ overflowX:'auto', width:'100%', display:'flex', justifyContent:'center' }}>{CertBody}</div>
        <button onClick={download} disabled={downloading} className="btn btn-primary" style={{ justifyContent:'center' }}>
          {downloading ? 'Generating PDF…' : '⬇ Download Certificate (PDF)'}
        </button>
      </div>
    );
  }

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:600, background:'rgba(0,0,0,0.92)', backdropFilter:'blur(12px)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div onClick={e=>e.stopPropagation()} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:16, maxWidth:'100%' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignSelf:'stretch', alignItems:'center' }}>
          <span style={{ color:'rgba(255,255,255,0.4)', fontSize:13 }}>{titleText} — {cert.recipient_name}</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={download} disabled={downloading} className="btn btn-primary" style={{ fontSize:'0.85rem' }}>
              {downloading ? 'Generating…' : '⬇ Download PDF'}
            </button>
            <button onClick={onClose} style={{ padding:'8px 14px', borderRadius:8, background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.15)', color:'#fff', cursor:'pointer', fontSize:13 }}>✕</button>
          </div>
        </div>
        <div style={{ overflowX:'auto', width:'100%', display:'flex', justifyContent:'center' }}>{CertBody}</div>
        <div style={{ fontSize:11, color:'rgba(255,255,255,0.25)', textAlign:'center' }}>
          QR code links to the verification page. Share with anyone to confirm authenticity.
        </div>
      </div>
    </div>
  );
}

export async function renderToCanvas(el) {
  const { default: html2canvas } = await import('html2canvas');
  return html2canvas(el, { scale:3, useCORS:true, allowTaint:true, backgroundColor:null, logging:false });
}

export async function downloadBulkCertificates(certs, format = 'zip') {
  const { default: jsPDF } = await import('jspdf');
  const { renderTempCert } = await import('./CertificateRenderer');
  if (format === 'merged') {
    const pdf = new jsPDF({ orientation:'landscape', unit:'mm', format:'a4' });
    const w = pdf.internal.pageSize.getWidth(), h = pdf.internal.pageSize.getHeight();
    for (let i = 0; i < certs.length; i++) {
      if (i > 0) pdf.addPage();
      const canvas = await renderTempCert(certs[i]);
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h);
    }
    pdf.save('MilleHills-Certificates-Bulk.pdf');
  } else {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    for (const cert of certs) {
      const pdf = new jsPDF({ orientation:'landscape', unit:'mm', format:'a4' });
      const w = pdf.internal.pageSize.getWidth(), h = pdf.internal.pageSize.getHeight();
      const canvas = await renderTempCert(cert);
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h);
      zip.file(`${cert.recipient_name.replace(/\s+/g,'-')}-Certificate.pdf`, pdf.output('blob'));
    }
    const blob = await zip.generateAsync({ type:'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'MilleHills-Certificates.zip'; a.click();
    URL.revokeObjectURL(url);
  }
}